import { useEffect, useState, type InputHTMLAttributes } from "react";

/**
 * Campo numérico que se puede borrar y reescribir sin pelear con él.
 * El problema que evita: un campo que, al quedar vacío, vuelve solo al número mínimo o al de antes (Number("") || 1), y entonces nunca se puede
 * borrar el 6 para escribir un 8 (queda "68"). Aquí el campo puede quedar vacío mientras se escribe; el valor solo se avisa cuando es un número
 * válido dentro de lo permitido, y al salir del campo se corrige (vacío o fuera de rango vuelve al último valor bueno o al límite).
 */
export function CampoNumero({ valor, alCambiar, min, max, ...resto }: {
  valor: number; alCambiar: (n: number) => void; min?: number; max?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "min" | "max">) {
  const [texto, setTexto] = useState(String(valor));
  // si el valor cambia desde afuera (por ejemplo, al elegir otro medicamento), el campo lo sigue; mientras la persona escribe no se toca
  useEffect(() => { setTexto((t) => (t !== "" && Number(t) === valor ? t : String(valor))); }, [valor]);
  const dentro = (n: number) => Number.isFinite(n) && (min === undefined || n >= min) && (max === undefined || n <= max);

  return (
    <input
      {...resto} type="number" min={min} max={max} value={texto}
      onChange={(e) => {
        const t = e.target.value;
        setTexto(t);
        if (t === "") return; // se está reescribiendo: no se vuelve a poner ningún número
        const n = Number(t);
        if (dentro(n)) alCambiar(n);
      }}
      onBlur={(e) => {
        const n = Number(texto);
        if (texto === "" || !Number.isFinite(n)) { setTexto(String(valor)); }
        else if (!dentro(n)) {
          const fijo = Math.min(max ?? n, Math.max(min ?? n, n));
          setTexto(String(fijo)); alCambiar(fijo);
        }
        resto.onBlur?.(e);
      }}
    />
  );
}
